// src/shell/Header.jsx
//
// The shell header: page-title slot (PageBand portals into it) and the avatar menu (profile,
// credits, plans, invoicing details, IT/EN, sign-out). «Piani» (/billing) is GREYED, with the
// tooltip naming who grants it, unless the user can buy plans (auth/canBuyPlans.js: a company
// admin, or a superadmin outside admin mode); /credits is not role-gated.
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, LogOut, User, FileText, CreditCard, Receipt } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { canBuyPlans } from '../auth/canBuyPlans.js';
import { setLocale } from '../i18n/locale.js';
import { COLORS, FONT_SANS, RADII } from '../constants/theme.js';
import { updateMe } from '../admin/adminApi.js';
import { userLabel, userInitial } from '../utils/userLabel.js';
import { PAGE_HEADER_SLOT_ID, HEADER_ROW_HEIGHT } from '../components/ui/PageBand.jsx';

export default function Header({ hamburgerBtn }) {
  const { user, activeTenant, logout, adminMode, isCustomerAdmin, isSuperadmin, setUser } = useAuth();
  const plansAllowed = canBuyPlans({ isCustomerAdmin, isSuperadmin, adminMode });
  const { t, i18n } = useTranslation('common');
  const lng = i18n.language?.split('-')[0] === 'en' ? 'en' : 'it';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // "Nome Cognome" when either part is set, otherwise the email.
  const displayName = userLabel(user);
  const initial = userInitial(user);
  const orgLabel = adminMode ? 'ESC Agency SRL' : activeTenant?.display_name || '';

  // Close the avatar menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // ⭐ ONE control for two things: the UI language of THIS browser, and
  // `users.locale` — the language of every transactional email.
  // ⛔ FAIL OPEN: the UI switches immediately and a failed PATCH is swallowed.
  // Blocking a language toggle on the network, or shouting about it, would be
  // worse than the emails lagging until the next switch.
  const chooseLocale = (code) => {
    setLocale(code);
    updateMe({ locale: code })
      .then((updated) => setUser(updated))
      .catch(() => { /* non-fatal by design */ });
  };

  const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    background: 'transparent',
    color: COLORS.ink,
    fontSize: '13px',
    fontFamily: FONT_SANS,
    cursor: 'pointer',
    borderRadius: RADII.control,
    textAlign: 'left',
  };

  return (
    <>
      <style>{`
        .shell-header {
          min-height: 82px;
          display: flex;
          /* v201: TOP, not centre — the avatar stays on the title row however tall the page header is
             (HEADER_ROW_HEIGHT in PageBand.jsx gives both the same height). */
          align-items: flex-start;
          justify-content: space-between;
          gap: 24px;
          padding: 20px 24px;
          background: ${COLORS.bg};
          flex-shrink: 0;
          font-family: ${FONT_SANS};
        }
        .shell-header-menuitem:hover { background: ${COLORS.field} !important; }
        @media (max-width: 768px) {
          .shell-header { min-height: 64px; padding: 12px; }
          .shell-header-identity { display: none !important; }
        }
      `}</style>
      <header className="shell-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          {hamburgerBtn}
          {/* Portal target: the page title mounts here — the Content Generator's title + breadcrumb
              (EscContentCreatorPro) and, since v193, every PageBand page (components/ui/PageBand.jsx). */}
          <div id={PAGE_HEADER_SLOT_ID} style={{ flex: 1, minWidth: 0 }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          {user?.email && (
            <div ref={menuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="shell-header-identity" style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25,
              }}>
                {/* Name + surname identify the user; the email shows only when both are empty (userLabel's
                    fallback), and otherwise rides in the tooltip. */}
                <span
                  title={displayName !== user.email ? user.email : undefined}
                  style={{ fontSize: '14px', fontWeight: 700, color: COLORS.ink, fontFamily: FONT_SANS }}
                >
                  {displayName}
                </span>
                {orgLabel && (
                  <span style={{ fontSize: '12px', color: COLORS.textSecondary, fontFamily: FONT_SANS }}>
                    {orgLabel}
                  </span>
                )}
              </div>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={displayName}
                onClick={() => setMenuOpen((o) => !o)}
                style={{
                  width: `${HEADER_ROW_HEIGHT}px`, height: `${HEADER_ROW_HEIGHT}px`, borderRadius: RADII.pill, flexShrink: 0,
                  border: 'none', padding: 0, cursor: 'pointer',
                  background: COLORS.accentTint,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: COLORS.accent, fontWeight: 700, fontSize: '15px', fontFamily: FONT_SANS,
                }}
              >
                {initial}
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 10px)',
                    right: 0,
                    minWidth: '220px',
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: RADII.control,
                    boxShadow: '0 8px 24px rgba(35,0,62,0.12)',
                    padding: '6px',
                    zIndex: 50,
                  }}
                >
                  {/* ⭐ The two READING pages sit above the divider; the account
                      and company-admin pages below it. Only /credits is ungated
                      here (readable by every member); «Piani» is greyed for
                      anyone who cannot buy plans (canBuyPlans). */}
                  <Link
                    role="menuitem"
                    className="shell-header-menuitem"
                    style={menuItemStyle}
                    to="/credits"
                    onClick={() => setMenuOpen(false)}
                  >
                    <CreditCard size={16} color={COLORS.textSecondary} />
                    {t('actions.credits')}
                  </Link>

                  {/* ⛔ GREYED, NEVER HIDDEN (v203): «Piani» sells plans and answers only a company admin (or a
                      superadmin outside admin mode) — canBuyPlans. «Crediti» above stays readable by every member. */}
                  {plansAllowed ? (
                    <Link
                      role="menuitem"
                      className="shell-header-menuitem"
                      style={menuItemStyle}
                      to="/billing"
                      onClick={() => setMenuOpen(false)}
                    >
                      <Receipt size={16} color={COLORS.textSecondary} />
                      {t('actions.plans')}
                    </Link>
                  ) : (
                    <span
                      role="menuitem"
                      aria-disabled="true"
                      title={t('actions.billingDetailsDisabled')}
                      style={{ ...menuItemStyle, opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      <Receipt size={16} color={COLORS.textSecondary} />
                      {t('actions.plans')}
                    </span>
                  )}

                  <div style={{ height: '1px', background: COLORS.border, margin: '6px 4px' }} />

                  <Link
                    role="menuitem"
                    className="shell-header-menuitem"
                    style={menuItemStyle}
                    to="/profile"
                    onClick={() => setMenuOpen(false)}
                  >
                    <User size={16} color={COLORS.textSecondary} />
                    {t('actions.profile')}
                  </Link>

                  {/* ⛔ GREYED, NEVER HIDDEN — the menu rule. A role-gated entry
                      stays visible and disabled with a tooltip naming who can
                      grant the right, so the menu never reshuffles by role and a
                      member learns where invoicing data lives and who to ask.
                      ⭐ A SUPERADMIN lands here too and that is CORRECT: customer
                      claims come from `customer_users` rows only, independent of
                      is_superadmin, so an SA has no "my company". They edit
                      customer records via /admin/tenants. Do not "fix" it. */}
                  {isCustomerAdmin ? (
                    <Link
                      role="menuitem"
                      className="shell-header-menuitem"
                      style={menuItemStyle}
                      to="/billing-details"
                      onClick={() => setMenuOpen(false)}
                    >
                      <FileText size={16} color={COLORS.textSecondary} />
                      {t('actions.billingDetails')}
                    </Link>
                  ) : (
                    <span
                      role="menuitem"
                      aria-disabled="true"
                      title={t('actions.billingDetailsDisabled')}
                      style={{ ...menuItemStyle, opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      <FileText size={16} color={COLORS.textSecondary} />
                      {t('actions.billingDetails')}
                    </span>
                  )}

                  <div style={{ padding: '8px 12px' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      fontSize: '12px', color: COLORS.textSecondary, fontFamily: FONT_SANS, marginBottom: '8px',
                    }}>
                      <Globe size={16} color={COLORS.textSecondary} />
                      {t('actions.language')}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {['it', 'en'].map((code) => {
                        const active = lng === code;
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => chooseLocale(code)}
                            style={{
                              flex: 1,
                              padding: '6px 0',
                              borderRadius: RADII.control,
                              border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
                              background: active ? COLORS.accentTint : COLORS.surface,
                              color: active ? COLORS.accent : COLORS.ink,
                              fontSize: '12px',
                              fontWeight: active ? 700 : 500,
                              fontFamily: FONT_SANS,
                              cursor: 'pointer',
                            }}
                          >
                            {code.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ height: '1px', background: COLORS.border, margin: '6px 4px' }} />

                  <button
                    type="button"
                    role="menuitem"
                    className="shell-header-menuitem"
                    style={menuItemStyle}
                    onClick={() => { setMenuOpen(false); logout(); }}
                  >
                    <LogOut size={16} color={COLORS.textSecondary} />
                    {t('actions.logout')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
}
